import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useResourceSkillApi custom hook for resource skill mapping operations.
 * - Provides CRUD operations for resource skill mappings using Apollo Client and GraphQL.
 * - Handles cache updates for resource skill mapping queries and mutations.
 * - Exposes helpers for reading/writing/updating resource skill mappings in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting resource skill mappings.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const RESOURCE_SKILL_QUERIES = {
  // Query to fetch all resource skill mappings
  GET_RESOURCE_SKILL_MAPPINGS: gql`
    query GetResourceSkillMappings {
      GetResourceSkillMappings {
        resource_skill_mapping_id
        resource_id
        skill_id
        year_of_experience
        proficient_level_enum
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new resource skill mapping
  ADD_RESOURCE_SKILL_MAPPING: gql`
    mutation AddResourceSkillMapping($resourceId: Int!, $skillId: Int!, $yearOfExperience: String, $proficientLevelEnum: ProficientLevel) {
      AddResourceSkillMapping(resource_id: $resourceId, skill_id: $skillId, year_of_experience: $yearOfExperience, proficient_level_enum: $proficientLevelEnum) {
        resource_skill_mapping_id
        resource_id
        skill_id
        year_of_experience
        proficient_level_enum
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing resource skill mapping
  UPDATE_RESOURCE_SKILL_MAPPING: gql`
    mutation UpdateResourceSkillMapping($resourceSkillMappingId: Int!, $resourceId: Int, $skillId: Int, $yearOfExperience: String, $proficientLevelEnum: ProficientLevel) {
      UpdateResourceSkillMapping(resource_skill_mapping_id: $resourceSkillMappingId, resource_id: $resourceId, skill_id: $skillId, year_of_experience: $yearOfExperience, proficient_level_enum: $proficientLevelEnum) {
        resource_skill_mapping_id
        resource_id
        skill_id
        year_of_experience
        proficient_level_enum
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a resource skill mapping
  DELETE_RESOURCE_SKILL_MAPPING: gql`
    mutation DeleteResourceSkillMapping($resourceSkillMappingId: Int!) {
      DeleteResourceSkillMapping(
        resource_skill_mapping_id: $resourceSkillMappingId
      ) {
        resource_skill_mapping_id
        resource_id
        skill_id
        year_of_experience
        proficient_level_enum
        is_active
        created_on
        updated_on
      }
    }
  `,
};

// Cache operations for resource skill mappings
const cacheOperations = {
  /**
   * Reads all resource skill mappings from Apollo cache.
   * @returns {Array} Array of resource skill mapping objects.
   */
  readResourceSkillMappings: () => {
    try {
      const { GetResourceSkillMappings = [] } =
        client.readQuery({
          query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
        }) || {};
      return GetResourceSkillMappings;
    } catch {
      return [];
    }
  },
  /**
   * Writes resource skill mappings to Apollo cache.
   * @param {Array} mappings
   */
  writeResourceSkillMappings: (mappings) => {
    client.writeQuery({
      query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
      data: { GetResourceSkillMappings: mappings },
    });
  },
  /**
   * Updates a resource skill mapping in Apollo cache.
   * @param {Object} updatedMapping
   */
  updateResourceSkillMappingInCache: (updatedMapping) => {
    const existingMappings = cacheOperations.readResourceSkillMappings();
    const updatedMappings = existingMappings.map((mapping) =>
      mapping.resource_skill_mapping_id ===
      updatedMapping.resource_skill_mapping_id
        ? updatedMapping
        : mapping
    );
    cacheOperations.writeResourceSkillMappings(updatedMappings);
  },
};

export const useResourceSkillApi = () => {
  // Add resource skill mapping mutation with cache update
  const [addResourceSkillMappingMutation] = useMutation(
    RESOURCE_SKILL_QUERIES.ADD_RESOURCE_SKILL_MAPPING,
    {
      update(cache, { data: { AddResourceSkillMapping } }) {
        try {
          const existingData = cache.readQuery({
            query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
          });
          if (existingData) {
            cache.writeQuery({
              query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
              data: {
                GetResourceSkillMappings: [
                  AddResourceSkillMapping,
                  ...existingData.GetResourceSkillMappings,
                ],
              },
            });
          }
        } catch (error) {
          console.error("Error updating cache after adding mapping:", error);
        }
      },
    }
  );

  // Update resource skill mapping mutation with cache update
  const [updateResourceSkillMappingMutation] = useMutation(
    RESOURCE_SKILL_QUERIES.UPDATE_RESOURCE_SKILL_MAPPING,
    {
      update(cache, { data: { UpdateResourceSkillMapping } }) {
        try {
          cacheOperations.updateResourceSkillMappingInCache(
            UpdateResourceSkillMapping
          );
        } catch (error) {
          console.error("Error updating cache after updating mapping:", error);
        }
      },
    }
  );

  // Delete resource skill mapping mutation with cache update
  const [deleteResourceSkillMappingMutation] = useMutation(
    RESOURCE_SKILL_QUERIES.DELETE_RESOURCE_SKILL_MAPPING,
    {
      update(cache, { data: { DeleteResourceSkillMapping } }) {
        try {
          const existingData = cache.readQuery({
            query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
          });
          if (existingData) {
            cache.writeQuery({
              query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
              data: {
                GetResourceSkillMappings:
                  existingData.GetResourceSkillMappings.filter(
                    (mapping) =>
                      mapping.resource_skill_mapping_id !==
                      DeleteResourceSkillMapping.resource_skill_mapping_id
                  ),
              },
            });
          }
        } catch (error) {
          console.error("Error updating cache after deleting mapping:", error);
        }
      },
    }
  );

  /**
   * Fetches all resource skill mappings.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourceSkillMappings = async () => {
    try {
      const { data } = await client.query({
        query: RESOURCE_SKILL_QUERIES.GET_RESOURCE_SKILL_MAPPINGS,
        fetchPolicy: "network-only",
      });
      if (!data || !data.GetResourceSkillMappings) {
        throw new Error("No data received from getResourceSkillMappings");
      }
      return { success: true, data: data.GetResourceSkillMappings };
    } catch (error) {
      console.error("Error in getResourceSkillMappings:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new resource skill mapping.
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createResourceSkillMapping = async (formData) => {
    try {
      const { data } = await addResourceSkillMappingMutation({
        variables: {
          resourceId: parseInt(formData.resource_id),
          skillId: parseInt(formData.skill_id),
          yearOfExperience: formData.year_of_experience,
          proficientLevelEnum: formData.proficient_level_enum,
        },
      });

      if (!data?.AddResourceSkillMapping) {
        throw new Error("Failed to create resource skill mapping");
      }

      return { success: true, data: data.AddResourceSkillMapping };
    } catch (error) {
      console.error("Error creating resource skill mapping:", error);
      return { 
        success: false, 
        error: error.message || "An error occurred while creating the resource skill mapping"
      };
    }
  };

  /**
   * Updates an existing resource skill mapping.
   * @param {number|string} mappingId
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateResourceSkillMapping = async (mappingId, formData) => {
    try {
      const { data } = await updateResourceSkillMappingMutation({
        variables: {
          resourceSkillMappingId: parseInt(mappingId),
          resourceId: parseInt(formData.resource_id),
          skillId: parseInt(formData.skill_id),
          yearOfExperience: formData.year_of_experience,
          proficientLevelEnum: formData.proficient_level_enum,
        },
      });

      if (!data?.UpdateResourceSkillMapping) {
        throw new Error("Failed to update resource skill mapping");
      }

      return { success: true, data: data.UpdateResourceSkillMapping };
    } catch (error) {
      console.error("Error updating resource skill mapping:", error);
      return { 
        success: false, 
        error: error.message || "An error occurred while updating the resource skill mapping"
      };
    }
  };

  /**
   * Deletes a resource skill mapping by its ID.
   * @param {number|string} resourceSkillMappingId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteResourceSkillMapping = async (resourceSkillMappingId) => {
    try {
      const { data } = await deleteResourceSkillMappingMutation({
        variables: { 
          resourceSkillMappingId: parseInt(resourceSkillMappingId)
        },
      });

      if (!data?.DeleteResourceSkillMapping) {
        throw new Error("Failed to delete resource skill mapping");
      }

      return { success: true, data: data.DeleteResourceSkillMapping };
    } catch (error) {
      console.error("Error deleting resource skill mapping:", error);
      return { 
        success: false, 
        error: error.message || "An error occurred while deleting the resource skill mapping"
      };
    }
  };

  return {
    getResourceSkillMappings,
    createResourceSkillMapping,
    updateResourceSkillMapping,
    deleteResourceSkillMapping,
    cacheOperations,
  };
};
